/**
 * Transcript Refiner that takes in query-hits, and refines query results
 */
export default class TranscriptRefiner {

    /**
     * Array of indexes from the search query hit
     */
    _hitList;

    /**
     * The search query
     */
    _query;

    /**
     * The search data
     */
    _data;

    /**
     * Constructor
     *
     * @param hitList the hit indexes list
     * @param query the search query
     * @param data the search data
     */
    constructor(hitList, query, data) {
        this._hitList = hitList;
        this._query = query;
        this._data = data;
    }

    /**
     * Accepts a string and split/tokenize it based on whitespaces
     *
     * @param query the query string to search
     * @returns {string[]} tokenized query string array
     */
    static splitQuery = (query) => query.trim().split(" ");

    /**
     * Accepts raw hit-list and filters down the exact-match hits (subjected to custom logic)
     *
     * @param hitList the raw hit-list
     * @returns {{}} hit-list with only exact-match hits
     * @private
     */
    _filterExactHits = (hitList) => {

        // checks if the given inputs fits the criteria of exact match
        const isExactMatch = (str, key) => {
            const isEqual = (str1, str2) => str1.toUpperCase() === str2.toUpperCase();
            const isConditionalMatch = (str, key) => {
                const stripEnd = [".", ",", "s", "'s", "es", "!", "?"];   // extra-tail
                return stripEnd.some((extraTail) => isEqual(str, (key + extraTail)));
            }

            return isEqual(str, key)                // case-insensitive equality
                || isConditionalMatch(str, key);    // conditional equality
        }

        const exactHitList = {}                             // exact-hit-list placeholder
        Object.entries(hitList).map(entry => {              // process each hit
            const key = entry[0];                           // current hit-key (searched-term-instance)
            const currentTermIndexes = entry[1];            // current hit-index for the key
            exactHitList[key] = [];                         // key's exact-hit-list placeholder
            currentTermIndexes.forEach((index) => {         // process each index
                if (isExactMatch(this._data[index].w, key)) {// if matches condition for exactness
                    exactHitList[key].push(index);          // push to key'sexact-hit-list
                }
            });
        });
        return exactHitList;
    }

    /**
     * Counts number of search-key-terms with at least one exact match
     *
     * @param exactHitList the exact-hit-list to probe
     * @returns {number} the number of query terms with exact match
     * @private
     */
    _countKeysWithExactMatch = (exactHitList) => {
        let count = 0;
        Object.entries(exactHitList).map(entry => {
            if (entry[1].length > 0) {  // if key's entry has at least one exact result
                count++;
            }
        });
        return count;
    }

    /**
     * Accepts hit-list & exact-hit-list and prepares conglomerate-hit-list
     *
     * @param exactHitList the hit list containing exact hits only
     * @param hitList   the hit list containing all the hits
     * @returns {*} hit list that combines exact and regular hits
     * @private
     */
    _prepareConglomerateHitList = (exactHitList, hitList) => {
        // conglomerate placeholder; use exact-hit-list as base
        const conglomerateHitList = Object.assign({}, exactHitList);

        // iterate and if no exact entries then use regular entries
        Object.entries(conglomerateHitList).map(entry => {
            const key = entry[0];                           // current search term
            const currentTermIndexes = entry[1];            // array of indexes for exact-match for search term
            if (currentTermIndexes.length === 0) {          // if no exact match
                conglomerateHitList[key] = hitList[key];    // use indexes from regular-list for the key
            }
        });
        return conglomerateHitList;
    }

    /**
     * Accepts a hit-list and combines adjacent hits
     *
     * @param list the hit-list
     * @returns {[]} arrays of combined-hit-arrays
     * @private
     */
    _mergeAdjacentHits = (list) => {
        const mergedList = [];  // overall-merged-groups placeholder

        const flatKeyIndexes = Object.keys(list).map((key) => list[key]);   // [key-1-hits],[key-2-hits],[key-3-hits]
        const flatIndexes = [].concat.apply([], flatKeyIndexes);            // [all-hits-flattened]

        // set ensures uniqueness, sort uses numeric comparison
        const flatSortedIndexes = [...new Set(flatIndexes)].sort((a,b)=>a-b);

        // checks if two indexes can be combined - if they are adjacent to one another
        const isCombinable = (index1, index2) => Math.abs(index1 - index2) === 1;

        // perform adjacent merge on all items (the list is updated when index gets merged)
        for (let i = 0; i < flatSortedIndexes.length; i++) {
            let currentHitGroupTail = flatSortedIndexes[i];   // current hit-index

            const currentGroup = [];    // current merge-group
            currentGroup[currentGroup.length] = currentHitGroupTail; // push initial tail as first hit-index

            // check the remaining indexes if combination is possible
            for (let j = i+1; j < flatSortedIndexes.length; j++) {
                const currentHitIndexToCheck = flatSortedIndexes[j];            // current index to check
                if (isCombinable(currentHitGroupTail,currentHitIndexToCheck)) { // check if combinable
                    // push to current hit group & update the tail of the hit-group
                    currentGroup[currentGroup.length] = currentHitIndexToCheck;
                    currentHitGroupTail = currentHitIndexToCheck;
                    // remove after merge & decrement index after hit-index removal
                    flatSortedIndexes.splice(j, 1);
                    j--;
                }
            }
            mergedList[mergedList.length] = currentGroup;   // push current-hit-group to overall-merged-groups
        }
        return mergedList;
    }

    /**
     * Checks if the hit-list contains the query in its entirety (hit-group with all search query terms)
     *
     * @param mergedHitList the merged-hit-list
     * @returns {*} flag indicating if the search query is present in its entirety
     * @private
     */
    _checkQueryMatch = (mergedHitList) => {
        return mergedHitList.some((hitGroup) => this._isAllSearchQueryAvailable(hitGroup));
    }

    /**
     * Filters hit-groups from the merged-hit-list that contains all search query terms
     *
     * @param mergedHitList the merged-hit-list
     * @returns {*} hit-groups with all search query terms
     * @private
     */
    _filterQueryMatchHits = (mergedHitList) => {
        return mergedHitList.filter((hitGroup) => this._isAllSearchQueryAvailable(hitGroup));
    }

    /**
     * Checks if given hit-group contains all search query terms
     *
     * @param hitGroup the hit-group with indexes
     * @returns {boolean|boolean} flag indicating if hit-group contains search-query
     * @private
     */
    _isAllSearchQueryAvailable = (hitGroup) => {
        const queryTerms = TranscriptRefiner.splitQuery(this._query);
        const isQueryWordsAtLeastSubStringOfHitGroup = (queryArr, hitIndexArr) => {
            return queryArr.every((queryTerm) => {
                const currentQueryTerm = queryTerm.toUpperCase();
                // each search-term should at least be sub-string of 1 word from hit-group (case-insensitive)
                return hitIndexArr.some((hitIndex) => {
                    const currentHitIndexWord = (this._data[hitIndex].w).toUpperCase();
                    return currentHitIndexWord.includes(currentQueryTerm);
                });
            });
        }
        return hitGroup.length === queryTerms.length                        // all covered
            && isQueryWordsAtLeastSubStringOfHitGroup(queryTerms, hitGroup); // every search term needs to be at-least sub-string
    }

    /**
     * Performs refinement of the regular hit-list into merged-hit-list
     *
     * @returns {*[]} merged-hit-list
     */
    performRefinement = () => {
        // prepare merged-hit-list
        const mergedHitList = this._mergeAdjacentHits(this._hitList);

        // prepare exact-merged-hit-list
        const exactHitList = this._filterExactHits(this._hitList);
        const exactMergedHitList = this._mergeAdjacentHits(exactHitList);

        // check query matches
        const isExactQueryMatch = this._checkQueryMatch(exactMergedHitList);
        const isQueryMatch = isExactQueryMatch || this._checkQueryMatch(mergedHitList); // query match

        /*
         * Final Combined-Hit-list
         * Cases:
         *  A.  (When complete query match is found)
         *      1. when the exact-merged-list contains the search-query
         *      2. when the merged-list partially contains the search-query
         *  B.  (When complete query match is not found)
         *      1. when all the search terms have exact matches - use the exact matches only
         *      2. when some search terms have exact terms - use (exact) and (partial) conglomerate
         *      3. when none of the search terms have exact terms - use the partial matches
         */
        let combinedHitList;    // combined-hit-list placeholder
        if (isQueryMatch) {
            combinedHitList = (isExactQueryMatch)
                ? this._filterQueryMatchHits(exactMergedHitList)    // Case A. 1.
                : this._filterQueryMatchHits(mergedHitList);        // Case A. 2.
        } else {

            const keysWithExactMatchCount = this._countKeysWithExactMatch(exactHitList);
            const allKeysHaveExactHit = Object.keys(exactHitList).length === keysWithExactMatchCount;
            const someKeysHaveExactHit = keysWithExactMatchCount > 0;

            if (allKeysHaveExactHit) {                  // Case B. 1.
                combinedHitList = exactMergedHitList;
            } else if (someKeysHaveExactHit) {          // Case B. 2.
                combinedHitList = this._mergeAdjacentHits(this._prepareConglomerateHitList(exactHitList, this._hitList));
            } else {                                    // Case B. 3.
                combinedHitList = this._mergeAdjacentHits(this._hitList);
            }
        }
        return combinedHitList;
    }
}